import {
  ASSET_TEXT_PROVENANCES,
  type AssetDescriptionSource,
  type AssetNameSource,
  type AssetTextProvenance,
} from "./vocabularies";
/**
 * Who wrote an asset's display text, and which of it a respondent may read.
 *
 * The one owner of both rules for the builder (Nest) and the editor: the
 * compiler's respondent projection of the asset catalog
 * (`buildRespondentAssetDescription`) and the editor's "not shown to
 * respondents" badge read the same answer (`assetTextReachesRespondents`).
 *
 * Provenance lives at `metadata.authored.<field>`:
 *   human        typed or confirmed by a person, or the user's own words a
 *                model copied (checked literal where the model writes it);
 *   adapted      a name a model wrote from the user's words about the asset
 *                (a chat, PLAN or paste name put right or written, B3);
 *   generated    written by a read of the file (an ad's, a page's, a video's or
 *                a PDF's), so it goes with the file;
 *   placeholder  written by something that never saw the creative.
 * Text in the builder's asset box reaches respondents whoever wrote it, a
 * placeholder excepted (owner decision, 14 October 2026): the box is shown
 * whole, beside the file of an asset shown as its file too (wave D, D2 step 3,
 * replacing D1). A file's read is never box text but its note
 * (`metadata.fileRead`), so nothing in the box is an account of the file.
 *
 * Every writer stamps what it writes; the write refuses an asset whose name or
 * description has none (`asset_text_provenance_missing`).
 */
export type { AssetTextProvenance };

/** The `metadata` key holding the per-field provenance record. */
export const ASSET_AUTHORED_METADATA_KEY = "authored";

type AssetWithMetadata = { metadata?: unknown } | null | undefined;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function isAssetTextProvenance(
  value: unknown,
): value is AssetTextProvenance {
  return ASSET_TEXT_PROVENANCES.includes(value as AssetTextProvenance);
}

/**
 * Provenance recorded for `name` / `description`, or undefined when nothing was
 * recorded (an asset stored before every writer stamped).
 */
export function readAssetTextProvenance(
  asset: AssetWithMetadata,
  field: "name" | "description",
): AssetTextProvenance | undefined {
  const metadata = isRecord(asset?.metadata) ? asset.metadata : undefined;
  const authored = metadata?.[ASSET_AUTHORED_METADATA_KEY];
  const value = isRecord(authored) ? authored[field] : undefined;
  return isAssetTextProvenance(value) ? value : undefined;
}

/**
 * Whether an asset's description is a content-free placeholder. Recorded
 * provenance wins; the legacy `descriptionIsPlaceholder` boolean is the
 * fallback for assets stored before provenance existed.
 */
export function isAssetDescriptionPlaceholder(asset: AssetWithMetadata): boolean {
  const provenance = readAssetTextProvenance(asset, "description");
  if (provenance) return provenance === "placeholder";
  const metadata = isRecord(asset?.metadata) ? asset.metadata : undefined;
  return metadata?.descriptionIsPlaceholder === true;
}

/**
 * The provenance a model writer's typed name source stamps on the name: the
 * user's exact words (`label`, `content`) are `human`, a name the model wrote
 * from them is `adapted`. The one mapping every model wire's write reads.
 */
export function nameSourceProvenance(
  source: AssetNameSource,
): AssetTextProvenance {
  return source === "adapted" ? "adapted" : "human";
}

/**
 * The provenance a model writer's typed description source stamps on the
 * description: the user's exact words (`user`) are `human`, a description the
 * model wrote from them is `adapted`.
 */
export function descriptionSourceProvenance(
  source: AssetDescriptionSource,
): AssetTextProvenance {
  return source === "adapted" ? "adapted" : "human";
}

/**
 * Whether an asset's `name` or `description` is text a respondent (and so a
 * twin) may read: anything in the builder's asset box, a person's text or the
 * platform's (a name a read or a model wrote), but never a placeholder or text
 * no writer recorded (owner decision, 14 October 2026). The box is shown whole
 * (wave D, D2 step 3).
 */
export function assetTextReachesRespondents(
  asset: AssetWithMetadata,
  field: "name" | "description",
): boolean {
  const provenance = readAssetTextProvenance(asset, field);
  return provenance !== undefined && provenance !== "placeholder";
}
